// plugins/logger.ts
import {
  defineNuxtPlugin,
  useNuxtApp,
  useRequestEvent,
  useRoute,
} from 'nuxt/app'
import type { RouteLocationNormalizedLoaded } from 'vue-router'

const DISABLE = false

export type LoggerPlugin = (
  event: string,
  metadata?: Record<string, unknown>,
) => Promise<void>

function gatherMetadata(ctx: {
  req?: any
  route: RouteLocationNormalizedLoaded
}): Record<string, unknown> {
  const { route, req } = ctx
  const isClient = process.client
  return {
    route: route
      ? {
          path: route.path,
          fullPath: route.fullPath,
          params: route.params,
          query: Object.fromEntries(
            Object.entries(route.query).map(([k, v]) => [
              k,
              Array.isArray(v) ? v.join(',') : (v ?? ''),
            ]),
          ),
        }
      : undefined,
    timestamp: Date.now(),
    userAgent: req
      ? req.headers['user-agent']
      : isClient
        ? navigator.userAgent
        : undefined,
    language: req
      ? req.headers['accept-language']
      : isClient
        ? navigator.language
        : undefined,
    screenSize: isClient
      ? { width: window.screen.width, height: window.screen.height }
      : undefined,
    isSSR: !isClient,
  }
}

export default defineNuxtPlugin(() => {
  const route = useRoute()
  const logEvent: LoggerPlugin = async (
    event: string,
    metadata?: Record<string, unknown>,
  ): Promise<void> => {
    if (DISABLE) {
      return
    }
    const req = useRequestEvent()?.node?.req
    const { $sessionId } = useNuxtApp()
    const fullPayload = {
      ...gatherMetadata({
        req,
        route,
      }),
      ...metadata,
      sessionId: process.client ? $sessionId : undefined,
    }

    const url = '/_/fallback/_/log/'
    const data = JSON.stringify({ event, metadata: fullPayload })

    if (process.client && navigator.sendBeacon) {
      // Use sendBeacon in browser context
      const blob = new Blob([data], { type: 'application/json' })
      navigator.sendBeacon(url, blob)
    } else {
      // Fallback for SSR or browsers without sendBeacon
      try {
        await $fetch(url, {
          method: 'POST',
          body: data,
          headers: { 'Content-Type': 'application/json' },
          timeout: 10000,
        })
      } catch (error) {
        console.warn('Failed to log event:', error)
      }
    }
  }

  return {
    provide: {
      log: logEvent,
    },
  }
})

// Type augmentation for Nuxt 3
declare module 'nuxt/app' {
  interface NuxtApp {
    $log: LoggerPlugin
  }
}
